/* eslint-disable react-hooks/exhaustive-deps */
import { DatePicker, Select } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { TFunction } from 'i18next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineLeft, AiOutlineRight, AiOutlineTeam } from 'react-icons/ai';
import { FiExternalLink } from 'react-icons/fi';
import { IoChevronBack, IoChevronForward, IoClose } from 'react-icons/io5';

import { FreePeriods } from '@core/booking/booking.types';
import { getFreePeriods } from '@core/booking/booking.service';

import { CityGroup, LocationWithRooms } from './meeting-room-landing.types';

// Constants
const ROW_HEIGHT = 80;
const SLOT_SIZE = 30;
const SLOT_HEIGHT = (SLOT_SIZE / 60) * ROW_HEIGHT; // 40px
const TIME_GUTTER = 64;
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MAX_VISIBLE_COLUMNS = 3;

function formatMinutes(minutes: number, has12Hours: boolean): string {
  const time = dayjs().startOf('day').add(minutes, 'minute');
  return time.format(has12Hours ? 'h:mm A' : 'HH:mm');
}

function parseExtraImages(extraImagesStr?: string): string[] {
  if (!extraImagesStr) return [];
  let data: unknown = extraImagesStr;
  if (typeof extraImagesStr === 'string') {
    try {
      data = JSON.parse(extraImagesStr);
    } catch {
      return [];
    }
  }
  return Array.isArray(data) ? data.filter((url) => typeof url === 'string' && url.length > 0) : [];
}

function cloudinaryUrl(url: string, width: number): string {
  if (!url || !url.includes('res.cloudinary.com')) return url;
  const transforms = `q_auto:best,f_auto,w_${width},c_fill,dpr_2.0`;
  return url.replace('/upload/', `/upload/${transforms}/`);
}

function formatDuration(minutes: number, t: TFunction): string {
  if (minutes < 60) return t('common.minutesShort', { count: minutes });
  const h = minutes / 60;
  return t('common.hoursShort', { count: h % 1 === 0 ? h : parseFloat(h.toFixed(1)) });
}

interface LandingCalendarViewProps {
  cityGroups: CityGroup[];
  clientName: string;
}

const LandingCalendarView: FC<LandingCalendarViewProps> = ({ cityGroups, clientName }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const phpURL = useMemo(() => `https://${clientName}.pickspace.com/api/app`, [clientName]);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Flatten all locations
  const allLocationsWithRooms = useMemo(() => {
    const result: LocationWithRooms[] = [];
    for (const group of cityGroups) {
      for (const loc of group.locations) {
        result.push(loc);
      }
    }
    return result;
  }, [cityGroups]);

  // State
  const [selectedLocationId, setSelectedLocationId] = useState<string>(
    () => allLocationsWithRooms[0]?.location.id || '',
  );
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [freePeriods, setFreePeriods] = useState<FreePeriods[]>([]);
  const [loadingFreePeriods, setLoadingFreePeriods] = useState(false);
  const [page, setPage] = useState(0);

  // Popover state
  const [popover, setPopover] = useState<{
    roomIndex: number;
    startMinutes: number;
    x: number;
    y: number;
  } | null>(null);
  const [popoverStart, setPopoverStart] = useState<number | null>(null);
  const [popoverEnd, setPopoverEnd] = useState<number | null>(null);

  // Image carousel modal state
  const [carouselModal, setCarouselModal] = useState<{ images: string[]; index: number; roomName: string } | null>(
    null,
  );

  // Per-room header image index for inline browsing
  const [roomImageIndices, setRoomImageIndices] = useState<Record<string, number>>({});

  // Default to first location when cityGroups change
  useEffect(() => {
    if (allLocationsWithRooms.length > 0 && !allLocationsWithRooms.some((l) => l.location.id === selectedLocationId)) {
      setSelectedLocationId(allLocationsWithRooms[0].location.id);
    }
  }, [allLocationsWithRooms]);

  const selectedLocationWithRooms = useMemo(
    () => allLocationsWithRooms.find((l) => l.location.id === selectedLocationId),
    [allLocationsWithRooms, selectedLocationId],
  );

  const rooms = useMemo(() => selectedLocationWithRooms?.rooms || [], [selectedLocationWithRooms]);
  const has12Hours = useMemo(
    () => (selectedLocationWithRooms?.location.has_12_hours || '1') === '1',
    [selectedLocationWithRooms],
  );

  const locationOptions = useMemo(
    () =>
      allLocationsWithRooms.map((l) => ({
        value: l.location.id,
        label: `${l.location.name}${l.location.address ? ` — ${l.location.address}` : ''}`,
      })),
    [allLocationsWithRooms],
  );

  // Fetch free periods
  useEffect(() => {
    if (!selectedLocationId || rooms.length === 0) {
      setFreePeriods([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingFreePeriods(true);
    getFreePeriods(
      phpURL,
      selectedDate.format('YYYY-MM-DD'),
      rooms.map((r) => r.id),
    )
      .then((res) => {
        if (!cancelled) setFreePeriods(res as FreePeriods[]);
      })
      .catch(() => {
        if (!cancelled) setFreePeriods([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingFreePeriods(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedLocationId, selectedDate, rooms, phpURL]);

  // Reset page when rooms change
  useEffect(() => {
    setPage(0);
  }, [rooms.length]);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const maxColumns = isMobile ? 1 : MAX_VISIBLE_COLUMNS;
  const totalPages = Math.ceil(rooms.length / maxColumns);
  const visibleRooms = rooms.slice(page * maxColumns, page * maxColumns + maxColumns);

  // Check if a slot is available
  const isSlotAvailable = useCallback(
    (roomIndex: number, minutes: number) => {
      const now = dayjs();
      const isToday = selectedDate.isSame(now, 'day');
      if (isToday && minutes < now.hour() * 60 + now.minute()) return false;
      return !!freePeriods?.[roomIndex]?.[minutes];
    },
    [freePeriods, selectedDate],
  );

  // Handle slot click
  const handleSlotClick = useCallback(
    (roomIndex: number, minutes: number, e: React.MouseEvent) => {
      if (!isSlotAvailable(roomIndex, minutes)) return;

      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setPopover({
        roomIndex,
        startMinutes: minutes,
        x: rect.left + rect.width / 2,
        y: rect.top,
      });
      setPopoverStart(minutes);

      const endTimes = freePeriods?.[roomIndex]?.[minutes] || [];
      const preferredEnd = minutes + 60;
      if (endTimes.includes(preferredEnd)) {
        setPopoverEnd(preferredEnd);
      } else if (endTimes.length > 0) {
        setPopoverEnd(endTimes[endTimes.length - 1]);
      } else {
        setPopoverEnd(null);
      }
    },
    [isSlotAvailable, freePeriods],
  );

  // Close popover on click outside
  useEffect(() => {
    if (!popover) return undefined;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const el = document.getElementById('landing-calendar-popover');
      if (el && !el.contains(target)) {
        // Don't close if clicking an antd Select dropdown (rendered in a portal)
        if (target.closest('.ant-select-dropdown')) return;
        setPopover(null);
      }
    };
    const timer = setTimeout(() => document.addEventListener('mousedown', handleClick), 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [popover]);

  // Navigate to booking page
  const handleBookNow = useCallback(() => {
    if (!popover || popoverStart == null || popoverEnd == null) return;
    const room = rooms[popover.roomIndex];
    if (!room) return;
    router.push(
      `/booking/meeting-room?clientName=${clientName}&locationId=${selectedLocationId}&roomId=${
        room.id
      }&date=${selectedDate.format('YYYY-MM-DD')}&startTime=${popoverStart}&endTime=${popoverEnd}`,
    );
  }, [popover, popoverStart, popoverEnd, rooms, selectedDate, clientName, selectedLocationId, router]);

  // Open image carousel
  const handleImageClick = useCallback((room: (typeof rooms)[0]) => {
    const allImages: string[] = [];
    if (room.image_link) allImages.push(room.image_link);
    allImages.push(...parseExtraImages(room.extra_images));
    if (allImages.length === 0) return;
    setCarouselModal({ images: allImages, index: 0, roomName: room.name });
  }, []);

  // Open room detail in new tab
  const handleOpenRoom = useCallback(
    (roomId: string) => {
      window.open(
        `/booking/meeting-room/${roomId}?clientName=${clientName}&locationId=${selectedLocationId}`,
        '_blank',
      );
    },
    [clientName, selectedLocationId],
  );

  // Auto-scroll to a reasonable time
  useEffect(() => {
    if (!calendarRef.current) return;
    const now = dayjs();
    const scrollMinutes = selectedDate.isSame(now, 'day') ? now.hour() * 60 : 8 * 60;
    calendarRef.current.scrollTop = Math.max(0, (scrollMinutes / 60) * ROW_HEIGHT - ROW_HEIGHT);
  }, [selectedDate, rooms.length, loadingFreePeriods]);

  // Popover start/end options
  const popoverStartOptions = useMemo(() => {
    if (!popover) return [];
    const roomFP = freePeriods?.[popover.roomIndex] || {};
    const now = dayjs();
    const isToday = selectedDate.isSame(now, 'day');
    const currentMinutes = now.hour() * 60 + now.minute();
    return Object.keys(roomFP)
      .map(Number)
      .filter((m) => !isToday || m >= currentMinutes)
      .sort((a, b) => a - b)
      .map((m) => ({ value: m, label: formatMinutes(m, has12Hours) }));
  }, [popover, freePeriods, selectedDate, has12Hours]);

  const popoverEndOptions = useMemo(() => {
    if (!popover || popoverStart == null) return [];
    const endTimes = freePeriods?.[popover.roomIndex]?.[popoverStart] || [];
    return endTimes
      .sort((a, b) => a - b)
      .map((m) => ({
        value: m,
        label: `${formatMinutes(m, has12Hours)} (${formatDuration(m - popoverStart, t)})`,
      }));
  }, [popover, popoverStart, freePeriods, has12Hours, t]);

  // Update end time when start changes
  useEffect(() => {
    if (popover && popoverStart != null) {
      const endTimes = freePeriods?.[popover.roomIndex]?.[popoverStart] || [];
      const preferredEnd = popoverStart + 60;
      if (endTimes.includes(preferredEnd)) {
        setPopoverEnd(preferredEnd);
      } else if (endTimes.length > 0) {
        setPopoverEnd(endTimes[endTimes.length - 1]);
      } else {
        setPopoverEnd(null);
      }
    }
  }, [popoverStart]);

  return (
    <div className="w-full">
      {/* Top bar: Location selector + Date navigation */}
      <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-4">
        <div className="w-full md:w-auto md:min-w-[300px]">
          <Select
            value={selectedLocationId || undefined}
            onChange={(v) => setSelectedLocationId(v)}
            options={locationOptions}
            placeholder={t('landing.calendar.selectLocation')}
            style={{ width: '100%' }}
            size="large"
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSelectedDate((prev) => prev.subtract(1, 'day'))}
            className="flex items-center justify-center w-8 h-8 rounded-full border-none cursor-pointer"
            style={{ backgroundColor: '#F0F0F0', color: '#404D61' }}
          >
            <AiOutlineLeft size={14} className="rtl:rotate-180" />
          </button>
          <DatePicker
            value={selectedDate}
            onChange={(d) => d && setSelectedDate(d)}
            format="dddd, MMMM D, YYYY"
            allowClear={false}
            bordered={false}
            suffixIcon={null}
            style={{ fontWeight: 600, fontSize: 16, color: '#404D61', width: 280 }}
          />
          <button
            type="button"
            onClick={() => setSelectedDate((prev) => prev.add(1, 'day'))}
            className="flex items-center justify-center w-8 h-8 rounded-full border-none cursor-pointer"
            style={{ backgroundColor: '#F0F0F0', color: '#404D61' }}
          >
            <AiOutlineRight size={14} className="rtl:rotate-180" />
          </button>
          <button
            type="button"
            onClick={() => setSelectedDate(dayjs())}
            className="px-3 py-1 rounded-full border-none cursor-pointer text-sm font-medium"
            style={{ backgroundColor: '#F0F0F0', color: '#404D61' }}
          >
            {t('landing.calendar.today')}
          </button>
        </div>
      </div>

      {/* Calendar */}
      {rooms.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <p style={{ color: '#707070' }}>{t('landing.calendar.noRoomsAtLocation')}</p>
        </div>
      ) : (
        <div className="relative bg-white rounded-xl" style={{ border: '1px solid #E5E7EB' }}>
          {/* Room Headers */}
          <div className="flex items-stretch" style={{ paddingInlineStart: TIME_GUTTER }}>
            {/* Pagination left arrow */}
            {rooms.length > maxColumns && (
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="absolute start-0 top-0 flex items-center justify-center border-none cursor-pointer z-10"
                style={{
                  backgroundColor: 'transparent',
                  color: page === 0 ? '#D1D5DB' : '#404D61',
                  width: TIME_GUTTER,
                  height: 160,
                }}
              >
                <AiOutlineLeft size={20} className="rtl:rotate-180" />
              </button>
            )}

            {visibleRooms.map((room) => {
              const allImages: string[] = [];
              if (room.image_link) allImages.push(room.image_link);
              allImages.push(...parseExtraImages(room.extra_images));
              if (allImages.length === 0) allImages.push('/placeholder.webp');
              const imgIndex = roomImageIndices[room.id] || 0;
              const hasMultiple = allImages.length > 1;

              return (
                <div
                  key={room.id}
                  className="flex-1 flex flex-col items-center p-3 min-w-0"
                  style={{ minWidth: 180, borderBottom: '1px solid #E5E7EB' }}
                >
                  <div className="flex items-center gap-1">
                    {hasMultiple && (
                      <button
                        type="button"
                        onClick={() =>
                          setRoomImageIndices((prev) => ({
                            ...prev,
                            [room.id]: (imgIndex - 1 + allImages.length) % allImages.length,
                          }))
                        }
                        className="flex items-center justify-center border-none cursor-pointer rounded-full flex-shrink-0"
                        style={{ width: 22, height: 22, backgroundColor: '#F0F0F0', color: '#404D61' }}
                      >
                        <AiOutlineLeft size={10} className="rtl:rotate-180" />
                      </button>
                    )}
                    <div
                      className="relative overflow-hidden rounded-lg cursor-pointer"
                      style={{ width: 120, height: 80, flexShrink: 0 }}
                      onClick={() => handleImageClick(room)}
                    >
                      <Image
                        alt={room.name}
                        src={cloudinaryUrl(allImages[imgIndex], 240)}
                        fill
                        style={{ objectFit: 'cover' }}
                        unoptimized
                      />
                      {hasMultiple && (
                        <div className="absolute bottom-1 left-0 right-0 flex items-center justify-center gap-1">
                          {allImages.map((_, i) => (
                            <span
                              key={i}
                              className="rounded-full"
                              style={{
                                width: 5,
                                height: 5,
                                backgroundColor: i === imgIndex ? '#FFFFFF' : 'rgba(255,255,255,0.5)',
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    {hasMultiple && (
                      <button
                        type="button"
                        onClick={() =>
                          setRoomImageIndices((prev) => ({
                            ...prev,
                            [room.id]: (imgIndex + 1) % allImages.length,
                          }))
                        }
                        className="flex items-center justify-center border-none cursor-pointer rounded-full flex-shrink-0"
                        style={{ width: 22, height: 22, backgroundColor: '#F0F0F0', color: '#404D61' }}
                      >
                        <AiOutlineRight size={10} className="rtl:rotate-180" />
                      </button>
                    )}
                  </div>
                  <span className="text-sm font-bold mt-1 truncate w-full text-center" style={{ color: '#404D61' }}>
                    {room.name}
                  </span>
                  <div className="flex items-center gap-1 text-xs" style={{ color: '#707070' }}>
                    <AiOutlineTeam size={12} />
                    <span>{t('landing.upTo', { count: Number(room.max_people_number) })}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleOpenRoom(room.id)}
                    className="flex items-center gap-1 mt-1 text-xs border-none cursor-pointer bg-transparent"
                    style={{ color: 'var(--main)' }}
                  >
                    <FiExternalLink size={10} />
                    <span>{t('landing.calendar.viewMoreInfo')}</span>
                  </button>
                </div>
              );
            })}

            {/* Pagination right arrow */}
            {rooms.length > maxColumns && (
              <div
                className="absolute end-0 top-0 flex items-center justify-center z-10"
                style={{ width: 32, height: 160 }}
              >
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="flex items-center justify-center border-none cursor-pointer bg-transparent"
                  style={{ color: page >= totalPages - 1 ? '#D1D5DB' : '#404D61' }}
                >
                  <AiOutlineRight size={20} className="rtl:rotate-180" />
                </button>
              </div>
            )}
          </div>

          {/* Scrollable calendar grid */}
          <div
            ref={calendarRef}
            className="relative overflow-y-auto"
            style={{ height: 'calc(100vh - 380px)', minHeight: 400 }}
          >
            <div className="relative" style={{ height: 24 * ROW_HEIGHT }}>
              {/* Hour labels */}
              <div className="absolute start-0 top-0" style={{ width: TIME_GUTTER, height: 24 * ROW_HEIGHT }}>
                {HOURS.map((h) => (
                  <div key={h} className="flex items-start justify-end pe-2" style={{ height: ROW_HEIGHT }}>
                    <span className="text-xs relative" style={{ top: -8, color: '#9CA3AF' }}>
                      {formatMinutes(h * 60, has12Hours)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Room columns */}
              <div className="flex" style={{ marginInlineStart: TIME_GUTTER, height: 24 * ROW_HEIGHT }}>
                {visibleRooms.map((room, colIndex) => {
                  const absoluteRoomIndex = page * maxColumns + colIndex;
                  return (
                    <div
                      key={room.id}
                      className="flex-1 relative"
                      style={{
                        minWidth: 180,
                        borderInlineStart: '1px solid #E5E7EB',
                        borderInlineEnd: colIndex === visibleRooms.length - 1 ? '1px solid #E5E7EB' : 'none',
                      }}
                    >
                      {HOURS.map((h) => (
                        <React.Fragment key={h}>
                          {[0, 30].map((offset) => {
                            const minutes = h * 60 + offset;
                            const available = isSlotAvailable(absoluteRoomIndex, minutes);
                            return (
                              <div
                                key={offset}
                                onClick={(e) => handleSlotClick(absoluteRoomIndex, minutes, e)}
                                className="w-full"
                                style={{
                                  height: SLOT_HEIGHT,
                                  borderTop: offset === 0 ? '1px solid #E5E7EB' : '1px dashed #F0F0F0',
                                  backgroundColor: available ? '#FFFFFF' : '#F3F4F6',
                                  cursor: available ? 'pointer' : 'default',
                                }}
                                onMouseEnter={(e) => {
                                  if (available) (e.currentTarget as HTMLDivElement).style.backgroundColor = '#EEF2FF';
                                }}
                                onMouseLeave={(e) => {
                                  (e.currentTarget as HTMLDivElement).style.backgroundColor = available
                                    ? '#FFFFFF'
                                    : '#F3F4F6';
                                }}
                              />
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* Now line */}
              {selectedDate.isSame(dayjs(), 'day') && (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    top: ((dayjs().hour() * 60 + dayjs().minute()) / 60) * ROW_HEIGHT,
                    insetInlineStart: TIME_GUTTER - 4,
                    insetInlineEnd: 0,
                    height: 2,
                    backgroundColor: '#EF4444',
                    zIndex: 5,
                  }}
                >
                  <div
                    className="absolute rounded-full"
                    style={{ width: 8, height: 8, backgroundColor: '#EF4444', top: -3, insetInlineStart: 0 }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Loading overlay */}
          {loadingFreePeriods && (
            <div
              className="absolute inset-0 flex items-center justify-center rounded-xl"
              style={{ backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 10 }}
            >
              <p style={{ color: '#707070' }}>{t('landing.calendar.loadingAvailability')}</p>
            </div>
          )}
        </div>
      )}

      {/* Booking Popover */}
      {popover && (
        <div
          id="landing-calendar-popover"
          className="rounded-xl shadow-xl p-4 border"
          style={{
            position: 'fixed',
            left: Math.min(popover.x, typeof window !== 'undefined' ? window.innerWidth - 280 : 500),
            top: Math.max(popover.y - 10, 10),
            transform: 'translateY(-100%)',
            zIndex: 1000,
            backgroundColor: '#FFFFFF',
            borderColor: '#E5E7EB',
            width: 260,
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-bold" style={{ color: '#404D61' }}>
              {rooms[popover.roomIndex]?.name}
            </span>
            <button
              type="button"
              onClick={() => setPopover(null)}
              className="text-sm border-none bg-transparent cursor-pointer px-1"
              style={{ color: '#9CA3AF', fontSize: 16 }}
            >
              {'×'}
            </button>
          </div>
          <p className="text-xs m-0 mb-3" style={{ color: '#707070' }}>
            {selectedDate.format('dddd, MMMM D, YYYY')}
          </p>

          <div className="mb-2">
            <label className="text-xs font-medium block mb-1" style={{ color: '#707070' }}>
              {t('landing.calendar.startTime')}
            </label>
            <Select
              value={popoverStart}
              onChange={(v) => setPopoverStart(v)}
              options={popoverStartOptions}
              size="small"
              style={{ width: '100%' }}
            />
          </div>

          <div className="mb-3">
            <label className="text-xs font-medium block mb-1" style={{ color: '#707070' }}>
              {t('landing.calendar.endTime')}
            </label>
            <Select
              value={popoverEnd}
              onChange={(v) => setPopoverEnd(v)}
              options={popoverEndOptions}
              size="small"
              style={{ width: '100%' }}
              disabled={popoverStart == null}
            />
          </div>

          <button
            type="button"
            onClick={handleBookNow}
            disabled={popoverStart == null || popoverEnd == null}
            className="w-full py-2 rounded-lg text-white text-sm font-semibold border-none cursor-pointer"
            style={{
              backgroundColor: popoverStart != null && popoverEnd != null ? 'var(--main)' : '#E5E7EB',
              color: popoverStart != null && popoverEnd != null ? '#FFFFFF' : '#9CA3AF',
            }}
          >
            {t('landing.bookNow')}
          </button>
        </div>
      )}

      {/* Image Carousel Modal */}
      {carouselModal && (
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 2000, backgroundColor: 'rgba(0,0,0,0.75)' }}
          onClick={() => setCarouselModal(null)}
        >
          <div
            className="relative"
            style={{ maxWidth: '90vw', maxHeight: '80vh', width: 800 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={() => setCarouselModal(null)}
              className="absolute -top-10 end-0 flex items-center justify-center border-none cursor-pointer bg-transparent"
              style={{ color: '#FFFFFF' }}
            >
              <IoClose size={28} />
            </button>

            {/* Room name */}
            <p className="absolute -top-10 start-0 m-0 text-white text-sm font-medium">
              {carouselModal.roomName}
              {carouselModal.images.length > 1 && ` (${carouselModal.index + 1}/${carouselModal.images.length})`}
            </p>

            {/* Image */}
            <div className="relative w-full overflow-hidden rounded-xl" style={{ height: '60vh' }}>
              <Image
                src={cloudinaryUrl(carouselModal.images[carouselModal.index], 1200)}
                alt={`${carouselModal.roomName} - ${carouselModal.index + 1}`}
                fill
                style={{ objectFit: 'contain' }}
                unoptimized
              />
            </div>

            {/* Prev / Next arrows */}
            {carouselModal.images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() =>
                    setCarouselModal((prev) =>
                      prev ? { ...prev, index: (prev.index - 1 + prev.images.length) % prev.images.length } : null,
                    )
                  }
                  className="absolute start-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center border-none cursor-pointer"
                  style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
                >
                  <IoChevronBack size={20} className="rtl:rotate-180" style={{ color: '#404D61' }} />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setCarouselModal((prev) =>
                      prev ? { ...prev, index: (prev.index + 1) % prev.images.length } : null,
                    )
                  }
                  className="absolute end-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center border-none cursor-pointer"
                  style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
                >
                  <IoChevronForward size={20} className="rtl:rotate-180" style={{ color: '#404D61' }} />
                </button>
              </>
            )}

            {/* Dots indicator */}
            {carouselModal.images.length > 1 && (
              <div className="flex items-center justify-center gap-2 mt-3">
                {carouselModal.images.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setCarouselModal((prev) => (prev ? { ...prev, index: i } : null))}
                    className="border-none cursor-pointer p-0 rounded-full"
                    style={{
                      width: 8,
                      height: 8,
                      backgroundColor: i === carouselModal.index ? '#FFFFFF' : 'rgba(255,255,255,0.4)',
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingCalendarView;
